import React from 'react';
import { Mail } from 'lucide-react';

interface SupportFooterProps {
  className?: string;
  variant?: 'light' | 'dark';
  showDirectoryLink?: boolean;
  minimal?: boolean;
}

const popularCategories = [
  { name: 'Accounting', slug: 'accounting' },
  { name: 'Project Management', slug: 'project-management' },
  { name: 'Sales & CRM', slug: 'sales-and-crm' },
  { name: 'Marketing', slug: 'marketing' },
  { name: 'E-commerce', slug: 'e-commerce' },
  { name: 'Scheduling', slug: 'scheduling' },
];

export function SupportFooter({ className = '', variant = 'light', showDirectoryLink = true, minimal = false }: SupportFooterProps) {
  const textColor = variant === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const linkColor = variant === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  const iconColor = variant === 'dark' ? 'text-gray-500' : 'text-gray-400';
  const borderColor = variant === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const mutedLinkColor = variant === 'dark' ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500';
  const headingColor = variant === 'dark' ? 'text-gray-300' : 'text-gray-600';

  if (minimal) {
    return (
      <footer className={`py-6 mt-auto ${className}`}>
        <div className="text-center">
          <p className={`text-sm ${textColor}`}>
            Questions?{' '}
            <a
              href="mailto:btoolmepro@gmail.com"
              className={`inline-flex items-center gap-1.5 font-medium transition-colors ${linkColor}`}
            >
              <Mail className={`w-4 h-4 ${iconColor}`} />
              btoolmepro@gmail.com
            </a>
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`py-8 mt-auto ${className}`}>
      <div className={`border-t ${borderColor} pt-8`}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className={`text-sm font-semibold ${headingColor} mb-3`}>Popular Categories</h4>
              <ul className={`space-y-2 text-sm ${mutedLinkColor}`}>
                {popularCategories.map((category) => (
                  <li key={category.slug}>
                    <a href={`/categories/${category.slug}`} className="transition-colors hover:underline">
                      {category.name} Software
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className={`text-sm font-semibold ${headingColor} mb-3`}>Resources</h4>
              <ul className={`space-y-2 text-sm ${mutedLinkColor}`}>
                <li>
                  <a href="/what-software-do-i-need-for-my-business" className="transition-colors hover:underline">
                    What Software Do I Need?
                  </a>
                </li>
                <li>
                  <a href="/tools-needed-to-start-a-business" className="transition-colors hover:underline">
                    Tools to Start a Business
                  </a>
                </li>
                <li>
                  <a href="/what-software-should-a-small-business-use" className="transition-colors hover:underline">
                    Small Business Software Guide
                  </a>
                </li>
                <li>
                  <a href="/for-trades" className="transition-colors hover:underline">
                    Software for Trades
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className={`text-sm font-semibold ${headingColor} mb-3`}>Explore</h4>
              <ul className={`space-y-2 text-sm ${mutedLinkColor}`}>
                <li>
                  <a href="/directory" className="transition-colors hover:underline">
                    Tool Directory
                  </a>
                </li>
                <li>
                  <a href="/tools" className="transition-colors hover:underline">
                    All Business Tools
                  </a>
                </li>
                <li>
                  <a href="/questionnaire" className="transition-colors hover:underline">
                    Get Personalized Recommendations
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className={`border-t ${borderColor} pt-6 text-center`}>
            <p className={`text-sm ${textColor}`}>
              Questions or need help?{' '}
              <a
                href="mailto:btoolmepro@gmail.com"
                className={`inline-flex items-center gap-1.5 font-medium transition-colors ${linkColor}`}
              >
                <Mail className={`w-4 h-4 ${iconColor}`} />
                btoolmepro@gmail.com
              </a>
            </p>
            <p className={`text-xs ${textColor} mt-2 opacity-75`}>
              We typically respond within 24 hours
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
